/**
 * useDarkMode
 *
 * Hook returning whether the document is in dark mode. Observes <html> class
 * changes and syncs with localStorage and system preference on first run.
 */
import { useState, useEffect } from 'react';

export default function useDarkMode() {
  // Initialize with direct check to ensure it's accurate on initial render
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    
    // Always prioritize the DOM class as the source of truth
    const hasDarkClass = document.documentElement.classList.contains('dark');
    
    // If there's a dark class, use that
    if (hasDarkClass) {
      return true;
    }
    
    // If no dark class, check localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      // If localStorage says dark but no class, add the class
      document.documentElement.classList.add('dark');
      return true;
    }
    
    // If localStorage says light or no preference, check system preference
    if (savedTheme === 'light') {
      return false;
    }
    
    // No saved preference, check system preference
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    if (systemPrefersDark) {
      document.documentElement.classList.add('dark');
    }
    
    return systemPrefersDark;
  });

  useEffect(() => {
    // Immediately check dark mode state on mount
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };
    
    // Check immediately to handle any potential flickering
    checkDarkMode();
    
    // Set up observer for changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDarkMode = document.documentElement.classList.contains('dark');
          setIsDark(isDarkMode);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

// Simple alias for the useDarkMode hook for easier semantic usage
export function useTheme() {
  return useDarkMode();
}